import {
  always, anyPass, applySpec, evolve,
  join, map, omit, pipe, propEq, reject, split,
} from 'rambda'
import {
  column,
  columnBoolean,
  columnEveryNth,
  columnJsonObject,
  columnNumber,
  doOnlyRequirements,
  factsToGenerate,
  no,
  notOnlyRequirements,
  yes,
} from './utils'
import { ARTICLE_BLUEPRINT_AFTER } from '../consts'
import { ARTICLE_TYPES } from '@store/modules/article'
import { FACT_TYPES } from '@store/modules/fact'
import { SECTION_POSITION_EXPORT } from '@store/modules/section'

const REQUIREMENT_SEPARATOR = ' | '

const spec = {
  category: column('A'),
  topic: column('B'),
  type: always(ARTICLE_TYPES.BUYING_GUIDE),
  siteName: column('C'),
  modifier: always('SHALLOW'),
  doCreateChildren: columnBoolean('AO'),
  doPickCoverImage: always(true),
  doPickProducts: always(true),
  doPost: always(false),
  sections: [
    {
      name: always('Introduction'),
      doCreateVariant: yes,
      doSelectImage: yes,
    },
    {
      name: always('Requirements'),
      doCreate: anyPass([
        columnBoolean('G'),
        doOnlyRequirements,
      ]),
      fields: {
        orderHidden: always([
          'Processor',
          'Graphics',
          'Memory',
          'Storage',
          'Display',
          'Battery',
        ].join(REQUIREMENT_SEPARATOR)),
        minimum: pipe(columnEveryNth(3, 6, 'H'), join(REQUIREMENT_SEPARATOR)),
        medium: pipe(columnEveryNth(3, 6, 'I'), join(REQUIREMENT_SEPARATOR)),
        maximum: pipe(columnEveryNth(3, 6, 'J'), join(REQUIREMENT_SEPARATOR)),
      },
      doCreateVariant: yes,
      doSelectImage: yes,
    },
    {
      name: always('Processor'),
      doCreate: notOnlyRequirements,
      doCreateVariant: notOnlyRequirements,
      fields: {
        minimum: column('H'),
        medium: column('I'),
        maximum: column('J'),
      },
      factsToGenerate,
      doSelectImage: yes,
    },
    {
      name: always('Graphics'),
      doCreate: notOnlyRequirements,
      doCreateVariant: notOnlyRequirements,
      fields: {
        minimum: column('K'),
        medium: column('L'),
        maximum: column('M'),
      },
      factsToGenerate,
      doSelectImage: yes,
    },
    {
      name: always('Memory'),
      doCreate: notOnlyRequirements,
      doCreateVariant: notOnlyRequirements,
      fields: {
        minimum: column('N'),
        medium: column('O'),
        maximum: column('P'),
      },
      factsToGenerate,
      doSelectImage: yes,
    },
    {
      name: always('Storage'),
      doCreate: notOnlyRequirements,
      doCreateVariant: notOnlyRequirements,
      fields: {
        minimum: column('Q'),
        medium: column('R'),
        maximum: column('S'),
      },
      factsToGenerate,
      doSelectImage: yes,
    },
    {
      name: always('Display'),
      doCreate: notOnlyRequirements,
      doCreateVariant: notOnlyRequirements,
      fields: {
        minimum: column('T'),
        medium: column('U'),
        maximum: column('V'),
      },
      factsToGenerate,
      doSelectImage: yes,
    },
    {
      name: always('Battery'),
      doCreate: notOnlyRequirements,
      doCreateVariant: notOnlyRequirements,
      fields: {
        minimum: column('W'),
        medium: column('X'),
        maximum: column('Y'),
      },
      factsToGenerate,
      doSelectImage: yes,
    },
    {
      name: always('FAQ'),
      factsToGenerate: always([
        FACT_TYPES.RECOMMENDATIONS_ALL,
        FACT_TYPES.REQUIREMENTS_ALL,
        FACT_TYPES.TOPIC_QUESTIONS,
      ]),
      positionExport: always(SECTION_POSITION_EXPORT.END),
      doCreate: columnBoolean('Z'),
      doCreateVariant: yes,
      doSelectImage: no,
      after: always([
        ARTICLE_BLUEPRINT_AFTER.RECOMMENDATIONS,
      ]),
    },
  ],
  criteria: {
    minimalScores: columnJsonObject('AN'),
    filtersLaptop: columnJsonObject('AP'),
    weights: {
      battery: columnNumber('AA'),
      bonus: columnNumber('AB'),
      cpu: columnNumber('AC'),
      graphics: columnNumber('AD'),
      memory: columnNumber('AE'),
      ports: columnNumber('AF'),
      resolution: columnNumber('AG'),
      screen: columnNumber('AH'),
      storageNvme: columnNumber('AI'),
      storageSpace: columnNumber('AJ'),
      storageSsd: columnNumber('AK'),
      weight: columnNumber('AL'),
    },
  },
}

export default pipe(
  split('\n'),
  map(pipe(
    split('\t'),
    applySpec(spec),
    evolve({
      sections: pipe(
        reject(propEq('doCreate', false)),
        reject(({ fields }) =>
          fields && ('minimum' in fields && !fields.minimum),
        ),
        map(omit(['doCreate'])),
      ),
    }),
  )),
)
